import {useRequest, useUpdateAbTestConfig} from '@aboutyou/data-fetcher';
import {
  ACTIVE_AB_TESTS_PATH,
  useAbTestExperimentsCookieUpdate,
  useAbTestUserIdCookie,
  useExtractAbTestsGroupsFromCookieCallback,
  useGetAbTestUserId,
} from '@aboutyou/feature-flags';
import {useIsOptimizelyConsentGiven} from '@aboutyou/storage';
import {useRef} from 'react';
import {v4 as uuidv4} from 'uuid';

export const InitializeAbTestsConditionally = () => {
  const isCookieAccepted = useIsOptimizelyConsentGiven();
  const [abTestUserId, setAbTestUserId] = useAbTestUserIdCookie();

  const setAbTestExperimentsCookie = useAbTestExperimentsCookieUpdate();
  const updateAbTestConfig = useUpdateAbTestConfig();
  const extractAbTestsGroupsFromCookieCallback =
    useExtractAbTestsGroupsFromCookieCallback();

  const getAbTestUserId = useGetAbTestUserId();
  // We user `useGetAbTestUserId` to determine if the current user is a returning user here. This callback not only checks the cookie store value of `ay-ab-test-user-id` cookie,
  // but also does a fallback and check for the cookie value directly in the document.cookie. This way we can disable the request for the returning users reliably.
  // using `useAbTestUserIdCookie` wouldn't work here because, the value returning by it is always undefined in the first render of the application, and then it gets updated with the browser value after hydration.
  // so relying on that would not be able to disable the request for returning users correctly.
  const shouldDisableRequestForReturningUsers = useRef(
    Boolean(getAbTestUserId()),
  ).current;

  useRequest(
    'query:activeAbTests',
    async () => {
      const userId = abTestUserId || uuidv4();
      const response = await fetch(`${ACTIVE_AB_TESTS_PATH}?userId=${userId}`);

      // The endpoint returns an empty body when the AB test middleware is not
      // available (e.g. no Optimizely SDK key configured locally). Skip
      // activation instead of failing the query on a JSON parse error.
      const abTests = response.ok ? await response.json() : null;

      return {
        userId,
        abTests,
      };
    },
    {
      /**
       * We only want to fetch the active ab tests once for a new user after they accept the cookie consent.
       * For returning the active abtest config would already be updated by the Cloudflare Worker. So we don't need to fetch or update it again on the client.
       * NB: we are identifiying a returning user by the `ay-ab-test-user-id` cookie. If the user deletes the cookie, we will set the cookie again and fetch the active ab tests on the client again.
       */
      disabled: Boolean(
        !isCookieAccepted || shouldDisableRequestForReturningUsers,
      ),
      onSuccess: data => {
        if (!data.abTests) {
          return;
        }

        const abTestCookieValue = Object.values(data.abTests)
          .map((abTest: any) => `${abTest.experimentKey}=${abTest.variation}`)
          .join('|');

        setAbTestUserId(data.userId);
        setAbTestExperimentsCookie(abTestCookieValue);

        // updates the grpc config.abTests[]
        updateAbTestConfig?.(
          extractAbTestsGroupsFromCookieCallback(abTestCookieValue),
        );
      },
    },
  );

  return null;
};
